import axios from 'axios';

/**
 * Canonical Reporting 2.0 API client. Talks to ribeye-api's generic
 * `POST /reporting-v2/report` endpoint (auth + whitelabel middleware). The OAT
 * bearer is attached automatically by the global axios interceptor
 * (AxiosProvider). `VITE_BASE_API_URL` already includes the `/api/v1` prefix
 * (same as `/reportingDashboard/gam/report`), so the path is relative.
 */

export interface ReportSort {
  field: string;
  dir: 'asc' | 'desc';
}

/** The exact-match filter keys — same key space `contains`/`excludes` below reuse. */
export type FilterColumnKey =
  | 'dsps'
  | 'advertiser_ids'
  | 'campaign_ids'
  | 'line_item_ids'
  | 'creative_ids'
  | 'product_ids'
  | 'publishers'
  | 'channel_ids'
  | 'device_ids';

export type FilterConditionOp = 'is' | 'is_not' | 'contains' | 'not_contains';

export interface FilterCondition {
  key: FilterColumnKey;
  op: FilterConditionOp;
  /** Multiple values OR under 'is'/'contains', AND under 'is_not'/'not_contains'. */
  values: string[];
}

export interface FilterGroup {
  op: 'AND' | 'OR';
  conditions: FilterCondition[];
}

export interface ReportRequest {
  dimensions: string[];
  metrics: string[];
  date_range: { start: string; end: string };
  dsps?: string[];
  whitelabel_ids?: string[];
  client_ids?: string[];
  advertiser_ids?: string[];
  campaign_ids?: string[];
  line_item_ids?: string[];
  /**
   * Ribeye grouping-entity ids (#4614): positive integers, max 50 per list. Per
   * tier send these OR the name key above, never both — both would AND.
   */
  ribeye_campaign_ids?: number[];
  ribeye_line_item_ids?: number[];
  creative_ids?: string[];
  product_ids?: string[];
  publishers?: string[];
  channel_ids?: string[];
  device_ids?: string[];
  /**
   * Wildcard/keyword variant of the exact-match fields above (FB-007): any
   * term matches via a case-insensitive substring match (backend: `LIKE
   * '%term%'`), multiple terms on one key OR together. See ribeye-api's
   * queryBuilder.ts `ReportFilters.contains`.
   */
  contains?: Partial<Record<FilterColumnKey, string[]>>;
  /** Same shape as `contains`, but excluding — every term must NOT match. */
  excludes?: Partial<Record<FilterColumnKey, string[]>>;
  /**
   * Boolean filter groups (FB-007): unlike every field above, a group can OR
   * conditions across DIFFERENT keys (e.g. channel contains "CTV" OR device is
   * "Mobile") — the composition the flat fields can't express, since they're
   * all AND'd together. Multiple groups AND with each other and with the rest
   * of the request. See ribeye-api's queryBuilder.ts `ReportFilters.groups`.
   */
  groups?: FilterGroup[];
  sort?: ReportSort[];
  pagination?: { limit?: number; offset?: number };
}

export type ReportRow = Record<string, string | number | null>;

export interface ReportResponse {
  data: { rows: ReportRow[]; total_rows: number };
  freshness: Record<string, string>;
  /**
   * Filter keys the API could not apply, because the fact table it had to read
   * carries no such column — grouping by geography, for instance, rules out
   * Device and Publisher. Absent when nothing was dropped.
   *
   * Worth surfacing rather than ignoring: the numbers come back looking
   * filtered while being unfiltered, which a reader cannot detect.
   */
  ignored_filters?: string[];
  /**
   * Metric/dimension keys this caller isn't permitted to see, dropped from
   * the request before it reached the query builder (e.g. `metrics.spend`,
   * `dimensions.dsp`). Absent when nothing was restricted — same "present
   * only when non-empty" convention as `ignored_filters` above and as
   * `ReportBatchResponse`'s own per-item `restricted_fields` below. The
   * backend already sends this on the single `/reporting-2.0/report`
   * response too; it just had no type here for a caller to read (flank#5054).
   */
  restricted_fields?: string[];
  query: { ms: number };
}

export const REPORTING_V2_REPORT_PATH = '/reporting-2.0/report';

export const REPORTING_V2_REPORT_BATCH_PATH = '/reporting-2.0/report-batch';

export const postReport = async (request: ReportRequest, signal?: AbortSignal): Promise<ReportResponse> => {
  const response = await axios.post(REPORTING_V2_REPORT_PATH, request, {
    signal,
    timeout: 150_000,
  });
  return response.data as ReportResponse;
};

export interface BatchItem {
  key: string;
  request: ReportRequest;
}

export interface ReportBatchResponse {
  data: {
    results: {
      key: string;
      rows: ReportRow[];
      total_rows: number;
      /**
       * Same field as ReportResponse.ignored_filters above, per batch item
       * (flank#4880): a geo-dimensioned widget's query drops device_ids/publishers
       * because GEOGRAPHY carries neither column, and the per-item batch response
       * reports it the same way the single-query endpoint does (see
       * FlankReportingController.ts's report-batch handler).
       */
      ignored_filters?: string[];
      /**
       * Metric/dimension/filter keys this caller isn't permitted to see, dropped
       * from the request before it reached the query builder (e.g.
       * `metrics.client_spend`). Absent when nothing was restricted — same
       * "present only when non-empty" convention as `ignored_filters` above.
       * Lets a widget tell "restricted" apart from "genuinely blank" instead of
       * rendering both as the same dash.
       */
      restricted_fields?: string[];
      /**
       * A field WAS permitted but got dropped anyway for a reason that isn't
       * "you can't see this" — e.g. the `ribeye_publisher` rollup's capped
       * raw-bundle fetch (flank#4643) hit its row cap, so the smallest
       * buckets may be undercounted. Same "present only when non-empty"
       * convention as `restricted_fields`.
       */
      truncated_fields?: string[];
    }[];
  };
  freshness: Record<string, string>;
  query: { ms: number };
}

/**
 * One round trip for many widget queries. The dashboard coalesces every query
 * fired in the same tick into a single batch (see reportBatcher) so a full page
 * load is one request instead of ~12 — and the backend bounds DB concurrency.
 */
export const postReportBatch = async (items: BatchItem[]): Promise<ReportBatchResponse> => {
  const response = await axios.post(REPORTING_V2_REPORT_BATCH_PATH, { items }, { timeout: 150_000 });
  return response.data as ReportBatchResponse;
};

export const REPORTING_V2_CLIENT_RECAP_PATH = '/reporting-2.0/client-recap';

export type ClientRecapTone = 'Professional' | 'Casual' | 'Enthusiastic';
export type ClientRecapLength = 'Short' | 'Medium' | 'Long';
export type ClientRecapFormat = 'Email' | 'Bullet Points' | 'Paragraph';

export interface ClientRecapMetrics {
  impressions?: number;
  spend?: number;
  clicks?: number;
  ctr?: number;
  reach?: number;
  vcr?: number;
}

export interface ClientRecapRequest {
  clientName?: string;
  dateStart: string;
  dateEnd: string;
  tone: ClientRecapTone;
  length: ClientRecapLength;
  format: ClientRecapFormat;
  totals?: ClientRecapMetrics;
  priorTotals?: ClientRecapMetrics;
  channelBreakdown?: { channel: string; impressions: number; spend?: number }[];
  topMarkets?: { label: string; impressions: number }[];
}

export interface ClientRecapResponse {
  narrative: string;
}

export const postClientRecap = async (request: ClientRecapRequest): Promise<ClientRecapResponse> => {
  const response = await axios.post(REPORTING_V2_CLIENT_RECAP_PATH, request, { timeout: 60_000 });
  return response.data as ClientRecapResponse;
};

export const REPORTING_V2_DV360_CONVERSIONS_PATH = '/reporting-2.0/dv360-conversions';

export interface Dv360ConversionsRequest {
  date_range: { start: string; end: string };
  whitelabel_ids?: string[];
  client_ids?: string[];
  advertiser_ids?: string[];
}

export interface Dv360ConversionMetrics {
  post_click_conversions: number;
  post_view_conversions: number;
  total_conversions: number;
}

export interface Dv360ConversionRow extends Dv360ConversionMetrics {
  date: string;
  floodlight_activity_id: number;
  floodlight_activity_name: string | null;
}

export interface Dv360ConversionsResponse {
  data: Dv360ConversionRow[];
  totals: Dv360ConversionMetrics;
  data_start: string | null;
  data_end: string | null;
}

export const postDv360Conversions = async (request: Dv360ConversionsRequest): Promise<Dv360ConversionsResponse> => {
  const response = await axios.post(REPORTING_V2_DV360_CONVERSIONS_PATH, request);
  return response.data as Dv360ConversionsResponse;
};

export const REPORTING_V2_DV360_LIVE_REACH_PATH = '/reporting-2.0/dv360-live-reach';

export type Dv360LiveReachBreakdown = 'total' | 'campaign' | 'device' | 'day';

export interface Dv360LiveReachRequest {
  date_range: { start: string; end: string };
  breakdown: Dv360LiveReachBreakdown;
  whitelabel_ids?: string[];
  client_ids?: string[];
  advertiser_ids?: string[];
}

export interface Dv360LiveReachRow {
  /** UR dimension value for the breakdown (campaign id, device name, ISO day); null for total. */
  key: string | null;
  impressions: number | null;
  /** Null where DV360 withholds reach (too little volume or below its privacy floor). */
  reach: number | null;
  frequency: number | null;
}

export type Dv360LiveReachResponse =
  | { status: 'pending' }
  | { status: 'done'; rows: Dv360LiveReachRow[]; certainty: 'lower' | 'approx' | 'platform' }
  | { status: 'failed' }
  | { status: 'unavailable'; reason: string };

export const REPORTING_V2_BEESWAX_LIVE_REACH_PATH = '/reporting-2.0/beeswax-live-reach';

/** Beeswax live reach (distinct IPs over the impression share) takes the same request and polling. */
export const postBeeswaxLiveReach = async (request: Dv360LiveReachRequest): Promise<Dv360LiveReachResponse> => {
  const response = await axios.post(REPORTING_V2_BEESWAX_LIVE_REACH_PATH, request, { timeout: 30_000 });
  return response.data as Dv360LiveReachResponse;
};

export const REPORTING_V2_GAM_LIVE_REACH_PATH = '/reporting-2.0/gam-live-reach';

/** GAM live reach (a GAM REACH report job, total and per day only) takes the same request and polling. */
export const postGamLiveReach = async (request: Dv360LiveReachRequest): Promise<Dv360LiveReachResponse> => {
  const response = await axios.post(REPORTING_V2_GAM_LIVE_REACH_PATH, request, { timeout: 30_000 });
  return response.data as Dv360LiveReachResponse;
};

export const REPORTING_V2_META_LIVE_REACH_PATH = '/reporting-2.0/meta-live-reach';

/** Meta live reach (Marketing API insights for one ad account) answers at once, without polling. */
export const postMetaLiveReach = async (request: Dv360LiveReachRequest): Promise<Dv360LiveReachResponse> => {
  const response = await axios.post(REPORTING_V2_META_LIVE_REACH_PATH, request, { timeout: 30_000 });
  return response.data as Dv360LiveReachResponse;
};

export const postDv360LiveReach = async (request: Dv360LiveReachRequest): Promise<Dv360LiveReachResponse> => {
  // Each call only advances the server-side state, so a stalled one is safe to abandon.
  const response = await axios.post(REPORTING_V2_DV360_LIVE_REACH_PATH, request, { timeout: 30_000 });
  return response.data as Dv360LiveReachResponse;
};
